import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { Hooks, Util } from "@xt/xtbase";
import Nav from "@xt/Nav";
import store from "store";
import TabSwitch from "@/components/Pages/Cfd/_cmpt/tabSwitch";
import HomeDepositDrawer from "@/components/Pages/Home/HomeAssetOverview/HomeDepositDrawer";
import { getAllowedFiatCodesForTabs, getDynamicFiatCodes, isFiatMarketCode } from "utils/getFiatCurrencyMap";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;
const { getUrl } = Util;

interface Props {
  children?: React.ReactNode;
}

const CfdLayout: React.FC<Props> = ({ children }) => {
  const router = useRouter();
  const t = useTranslation();
  const { name } = store.market;
  const { isLogin, userInfo } = store.user;
  const [depositDrawerOpen, setDepositDrawerOpen] = useState(false);
  const [depositPreferredFiat, setDepositPreferredFiat] = useState<string | null>(null);
  const [showFiatTab, setShowFiatTab] = useState<boolean>(false);
  const [showFiatTabReady, setShowFiatTabReady] = useState(false);
  const [preferredFiatSymbol, setPreferredFiatSymbol] = useState<string>("btc_aed_cfd");
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  const resolveRunIdRef = useRef(0);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 法币 tab 直接跟随推荐币种缓存，判断结束前不触发跳转，避免 tab 闪烁。
  useEffect(() => {
    // 已登录但 userInfo 尚未加载（仍为 null）时，不急于决策，避免误判后把法币页重定向到 USDT
    if (isLogin && userInfo == null) {
      return;
    }

    const currentRunId = ++resolveRunIdRef.current;

    const runResolve = () => {
      const resolve = async () => {
        const codes = await getAllowedFiatCodesForTabs(userInfo?.fiatCurrencyIds);
        // 仅应用本次 effect 的异步结果，避免后续 effect 或竞态把已展示的法币 tab 覆盖掉
        if (currentRunId !== resolveRunIdRef.current) return;
        // 有允许的法币即展示法币本位 tab（1/2/3 都不命中时返回空数组 → 不展示）
        const showFiat = codes.length > 0;
        const first = codes[0] ? codes[0].toUpperCase() : null;
        setShowFiatTab(showFiat);
        setPreferredFiatSymbol(showFiat && first ? `btc_${first.toLowerCase()}_cfd` : "btc_usdt_cfd");
        setShowFiatTabReady(true);
      };
      resolve();
    };

    const symbol = router.query?.symbol as string;
    const quote = symbol?.split("_")[1]?.toUpperCase();
    const isFiatUrl = quote && isFiatMarketCode(quote);
    // 当前在法币合约页且未确认登录态时，短延再决策，避免首屏 appState 未就绪时误判导致误重定向
    if (isFiatUrl && !isLogin) {
      const t = setTimeout(runResolve, 300);
      return () => clearTimeout(t);
    }
    runResolve();
  }, [isLogin, userInfo, userInfo?.fiatCurrencyIds]);

  // 登录后拉取收藏列表，与现货 trade market container 一致，保证进入已收藏币对时收藏按钮显示已收藏状态
  useEffect(() => {
    if (isLogin) store.user.getSymbolStar();
  }, [isLogin]);

  // 仅在判断结束后：无 Fiat-M 且当前在法币页时才跳转到 USDT-M
  // 已登录且 userInfo 尚未加载时绝不重定向，避免首屏 isLogin 未就绪时误判导致误跳
  useEffect(() => {
    if (!showFiatTabReady || showFiatTab) return;
    if (isLogin && userInfo == null) return;
    const { symbol } = router.query;
    const currentSymbol = (symbol as string) || name || "";
    const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase();
    if (quoteCurrency && isFiatMarketCode(quoteCurrency)) {
      // router.replace("/futures/contract/btc_usdt_cfd");
    }
  }, [showFiatTabReady, showFiatTab, isLogin, userInfo, router.query.symbol, name, router]);

  useEffect(() => {
    const clearTransitionTimer = () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };

    const handleRouteStart = () => {
      clearTransitionTimer();
      setIsRouteTransitioning(true);
    };

    const handleRouteDone = () => {
      clearTransitionTimer();
      transitionTimerRef.current = setTimeout(() => {
        setIsRouteTransitioning(false);
        transitionTimerRef.current = null;
      }, 260);
    };

    router.events.on("routeChangeStart", handleRouteStart);
    router.events.on("routeChangeComplete", handleRouteDone);
    router.events.on("routeChangeError", handleRouteDone);

    return () => {
      clearTransitionTimer();
      router.events.off("routeChangeStart", handleRouteStart);
      router.events.off("routeChangeComplete", handleRouteDone);
      router.events.off("routeChangeError", handleRouteDone);
    };
  }, [router.events]);

  // 从路由路径和当前 symbol 判断当前 tab 类型
  const activeTab = useMemo(() => {
    const path = router.pathname || router.asPath;
    if (path.includes("/futures/contract/spot")) {
      return "spot" as const;
    }
    if (path.includes("/futures/contract/")) {
      const { symbol } = router.query;
      const currentSymbol = (symbol as string) || name || "btc_brl_cfd";
      const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase() || "USDT";
      const isFiat = isFiatMarketCode(quoteCurrency);
      return isFiat ? ("fiatContract" as const) : ("uContract" as const);
    }
    if (path.includes("/trade/spot/") || path.includes("/futures/spot/")) {
      return "spot" as const;
    }
    const { symbol } = router.query;
    const currentSymbol = (symbol as string) || name || "btc_usdt_cfd";
    const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase() || "USDT";
    const isFiat = isFiatMarketCode(quoteCurrency);
    return isFiat ? ("fiatContract" as const) : ("uContract" as const);
  }, [router.pathname, router.asPath, router.query.symbol, name]);

  // 未就绪时只展示 USDT-M 且视为选中，避免 tab 闪烁、无选中态
  const displayActiveTab = !showFiatTabReady ? ("uContract" as const) : activeTab;
  const displayShowFiatTab = showFiatTabReady && showFiatTab;

  // 获取当前币种
  const currency = useMemo(() => {
    return name ? name.split("_")[0].toLowerCase() : "usdt";
  }, [name]);

  // 按优先级返回法币交易对：优先 preferredFiatSymbol（fiatCurrencyIds / IP 决策结果）
  const getFirstAvailableFiatSymbol = useCallback(() => {
    const quote = preferredFiatSymbol.split("_")[1]?.toUpperCase();
    if (quote && isFiatMarketCode(quote)) return preferredFiatSymbol;
    const { config } = store.market;
    if (!config) return "btc_aed_cfd";
    const baseCurrencies = ["btc", "eth", "usdt"];
    const fiatCodes = getDynamicFiatCodes();
    for (const base of baseCurrencies) {
      for (const fiat of fiatCodes) {
        const symbol = `${base}_${fiat.toLowerCase()}_cfd`;
        if (config[symbol] && config[symbol].state !== "DELISTED") return symbol;
      }
    }
    return "btc_aed_cfd";
  }, [preferredFiatSymbol, store.market.config]);

  // 处理 tab 切换
  const handleTabChange = useCallback(
    (tab: "uContract" | "fiatContract" | "spot") => {
      if (tab === "spot") {
        router.push("/futures/contract/spot");
        return;
      }

      const { symbol } = router.query;
      const currentSymbol = (symbol as string) || "btc_usdt_cfd";

      if (tab === "uContract") {
        // 切换到U本位合约：确保是U本位交易对（非法币）
        // 如果当前是法币交易对，切换到默认的U本位交易对
        const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase();
        if (isFiatMarketCode(quoteCurrency)) {
          // 切换到默认的U本位交易对
          router.push(`/futures/contract/btc_usdt_cfd`);
        } else {
          const cfdSymbol = currentSymbol.endsWith("_cfd") ? currentSymbol : `${currentSymbol}_cfd`;
          // 如果已经是正确的路由，不需要跳转
          if (router.asPath !== `/futures/contract/${cfdSymbol}`) {
            router.push(`/futures/contract/${cfdSymbol}`);
          }
        }
      } else if (tab === "fiatContract") {
        // 切换到法币本位合约：查找第一个可用的法币交易对
        const fiatSymbol = getFirstAvailableFiatSymbol();
        router.push(`/futures/contract/${fiatSymbol}`);
      }
    },
    [router, getFirstAvailableFiatSymbol]
  );

  // 处理充值按钮点击：未登录跳转登录页，已登录打开首页同款充值弹窗
  const handleDepositClick = useCallback(() => {
    if (!isLogin) {
      location.href = getUrl("/accounts/login?backurl=" + encodeURIComponent(typeof location !== "undefined" ? location.href : ""));
      return;
    }
    let preferredFiat: string | null = null;
    if (activeTab === "fiatContract") {
      const quoteCurrency = name ? name.split("_")[1]?.toUpperCase() : null;
      if (quoteCurrency && quoteCurrency !== "USDT") {
        preferredFiat = quoteCurrency;
      }
    }
    setDepositPreferredFiat(preferredFiat);
    setDepositDrawerOpen(true);
  }, [isLogin, activeTab, name]);

  const closeDepositDrawer = useCallback(() => {
    setDepositDrawerOpen(false);
    setDepositPreferredFiat(null);
  }, []);

  return (
    <div className={styles.page}>
      {/* 开启 bottomBar */}
      <Nav mode="fixed" bgColor="#181a20" bottomBar bottomBarHideTop />
      <div className={styles.pageWrapper}>
        {/* Tab切换组件 */}
        <TabSwitch
          activeTab={displayActiveTab}
          onTabChange={handleTabChange}
          onDepositClick={handleDepositClick}
          showFiatTab={displayShowFiatTab}
        />

        {/* 子路由内容 */}
        <div className={`${styles.contentWrapper} ${isRouteTransitioning ? styles.contentWrapperTransitioning : ""}`}>{children}</div>
      </div>

      <HomeDepositDrawer
        open={depositDrawerOpen}
        onClose={closeDepositDrawer}
        preferredFiatCode={depositPreferredFiat}
      />
    </div>
  );
};

export default observer(CfdLayout);
